import { ChatWindowsContext } from "@/context/ChatWindowsContext";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { ChatWindow } from "@/pages/Messaging/ChatWindow/types";
import { Severity } from "@velaro/velaro-component-lib";
import React, { useState, useEffect, useCallback } from "react";

export function ChatWindowsProvider(props: { children: React.ReactNode }) {
  const api = useApi();
  const [chatWindows, setChatWindows] = useState<ChatWindow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const toastAlert = useToastAlert();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const json = await api.messaging.get("ChatWindow/List");
      const result = (await json.json()) as ChatWindow[];
      setChatWindows(Array.isArray(result) ? result : []);
    } catch (err) {
      toastAlert.displayToast(
        Severity.Error,
        `Failed to load chat windows: ${err instanceof Response ? err.status : String(err)}`
      );
      setChatWindows([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  async function createChatWindow(
    name: string
  ): Promise<ChatWindow | undefined> {
    if (saving) return;
    setSaving(true);
    const response = await api.messaging.post("ChatWindow/Create", { name });
    if (!response.ok) {
      const errorText = await response.text();
      setSaving(false);
      toastAlert.displayToast(Severity.Error, errorText);
      return;
    }
    const newChatWindow = (await response.json()) as ChatWindow;
    setChatWindows((prev) => [...prev, newChatWindow]);
    toastAlert.displayToast(
      Severity.Success,
      "Chat window created successfully."
    );
    setSaving(false);
    return newChatWindow;
  }

  async function deleteChatWindow(id: number) {
    if (saving) return;
    setSaving(true);
    const response = await api.messaging.delete(`ChatWindow/Delete/${id}`);
    if (!response.ok) {
      const errorText = await response.text();
      setSaving(false);
      toastAlert.displayToast(Severity.Error, errorText);
      return;
    }
    setChatWindows((prev) => prev.filter((window) => window.id !== id));
    toastAlert.displayToast(
      Severity.Success,
      "Chat window deleted successfully."
    );
    setSaving(false);
  }

  async function getChatWindowById(
    id: number
  ): Promise<ChatWindow | undefined> {
    const response = await api.messaging.get(`ChatWindow/Get/${id}`);
    if (!response.ok) {
      const errorText = await response.text();
      toastAlert.displayToast(Severity.Error, errorText);
      return undefined;
    }
    return (await response.json()) as ChatWindow;
  }

  return (
    <ChatWindowsContext.Provider
      value={{
        chatWindows,
        loading,
        createChatWindow,
        deleteChatWindow,
        saving,
        getChatWindowById
      }}
    >
      {props.children}
    </ChatWindowsContext.Provider>
  );
}
